import {
  createFileRoute,
  ErrorComponentProps,
  isRedirect,
  Navigate,
  NavigateOptions,
  notFound,
  redirect,
  useParams,
} from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { AxiosError } from '@mint-lib/api';
import { InternalServerErrorPage } from '@mint-lib/error-boundary';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { buildGuards, hasFlagEnabled } from '@mint-lib/routing-guards';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

const importHubV2Guard = buildGuards(hasFlagEnabled('importHubV2'));

type DataFileClientStepNames =
  | DataIngestion.DataFile.TableDataFilePreviewQueryResult['currentStep']
  | 'header_selection';

const getDataFileClientStep = (
  dataFilePreview: DataIngestion.DataFile.TableDataFilePreviewQueryResult,
): DataFileClientStepNames => {
  const {
    currentStep,
    status,
    isFileUploaded,
    templateSheetsMismatched,
    templateHeadersMismatched,
  } = dataFilePreview;

  if (currentStep === 'upload') {
    const isUploading = status === 'uploading';
    const isUploadStep =
      (!isFileUploaded && !isUploading) ||
      templateSheetsMismatched ||
      templateHeadersMismatched;

    return isUploadStep ? 'upload' : 'header_selection';
  }

  return currentStep;
};

const getStepRouteLink = (
  step: DataFileClientStepNames,
  params: {
    dataFileId: string;
  },
): NavigateOptions => {
  if (step === 'upload') {
    return {
      to: '/company-profile/import-hub/performance/imports/$dataFileId/upload',
      params,
    };
  }

  if (step === 'header_selection') {
    return {
      to: '/company-profile/import-hub/performance/imports/$dataFileId/header-selection',
      params,
    };
  }

  if (step === 'headers_mapping') {
    return {
      to: '/company-profile/import-hub/performance/imports/$dataFileId/headers-mapping',
      params,
    };
  }

  if (step === 'validation') {
    return {
      to: '/company-profile/import-hub/performance/imports/$dataFileId/validation',
      params,
      search: {
        onlyErrors: false,
        itemsPerPage: 50,
      },
    };
  }

  if (step === 'channel_assignment') {
    return {
      to: '/company-profile/import-hub/performance/imports/$dataFileId/data-assignment',
      params,
      search: {
        itemsPerPage: 50,
      },
    };
  }

  return {
    to: '/company-profile/import-hub/performance',
    params,
    search: {
      tab: 'imports',
    },
  };
};

const isAxiosError = (error: unknown): error is AxiosError => {
  if (!error || typeof error !== 'object') return false;
  const err = error as Record<string, unknown>;
  return err.name === 'AxiosError' || 'response' in err;
};

export const Route = createFileRoute(
  '/_secured/company-profile/import-hub/performance/imports/$dataFileId',
)({
  async beforeLoad({ context, buildLocation }) {
    const hasModifyPermission = await context.hasPermission(
      'dataManagementHubFeature:modify',
    );
    if (!hasModifyPermission) {
      throw notFound();
    }
    return { buildLocation };
  },
  loader: {
    handler: async ({ context, params, location, cause }) => {
      const { t } = await context.getInstance('i18n', '@myn/import-hub');

      const queryClient =
        await context.serviceLocator.getOrThrowInstance('queryClient');

      const armGuardsContext = await context.getGuardContext(
        await context.getInstance('flagsmith', '@mint/arm'),
      );
      const isImportHubV2 = importHubV2Guard(armGuardsContext);

      const dataFilePreviewV1QueryOptions =
        DataIngestion.DataFile.getTableDataFilePreviewV1QueryOptions({
          urlParams: { id: params.dataFileId },
        });

      const dataFilePreviewV2QueryOptions =
        DataIngestion.DataFile.getTableDataFilePreviewV2QueryOptions({
          urlParams: { id: params.dataFileId },
        });

      try {
        const dataFilePreview = isImportHubV2
          ? await queryClient.fetchQuery({
              ...dataFilePreviewV2QueryOptions,
              staleTime: 30 * 1000,
            })
          : await queryClient.fetchQuery({
              ...dataFilePreviewV1QueryOptions,
              staleTime: 30 * 1000,
            });

        const clientStepName = getDataFileClientStep(dataFilePreview);
        const stepRouteLink = getStepRouteLink(clientStepName, params);
        const stepRouteLocation = context.buildLocation(stepRouteLink);
        const isInvalidPath = location.pathname !== stepRouteLocation.pathname;

        if (isInvalidPath) {
          throw redirect({
            ...stepRouteLink,
            replace: true,
          });
        }

        return { stepRouteLink };
      } catch (error) {
        if (isRedirect(error)) {
          throw error;
        }

        if (cause === 'preload') {
          return;
        }

        if (!isAxiosError(error)) {
          throw error;
        }

        if ([400, 404, 422, 425].includes(error.response?.status ?? 0)) {
          if (cause === 'enter') {
            if (error.response?.status === 400) {
              enqueueSnackbar({
                variant: 'error',
                subTitle: t('This wizard does not exist anymore.'),
                removable: true,
              });
            }
            if (error.response?.status === 425) {
              enqueueSnackbar({
                variant: 'error',
                subTitle: t(
                  'This file is currently being validated. Please try again in a couple of minutes.',
                ),
                removable: true,
              });
            }
          }
          throw redirect({
            to: '/company-profile/import-hub/performance',
            params,
            search: {
              tab: 'imports',
            },
          });
        }

        throw error;
      }
    },
    staleReloadMode: 'blocking',
  },
  errorComponent: function DataFileErrorComponent({
    error,
  }: ErrorComponentProps) {
    const { dataFileId } = useParams({
      from: '/_secured/company-profile/import-hub/performance/imports/$dataFileId',
    });

    const err = error as unknown as {
      response?: { status?: number };
      to?: string;
      params?: Record<string, string>;
      search?: Record<string, unknown>;
      replace?: boolean;
    };
    const status = err?.response?.status;
    const snackbarShown = useRef(false);

    useEffect(() => {
      if (snackbarShown.current) return;
      if (status === 400) {
        snackbarShown.current = true;
        enqueueSnackbar({
          variant: 'error',
          subTitle: 'This wizard does not exist anymore.',
          removable: true,
        });
      }
      if (status === 425) {
        snackbarShown.current = true;
        enqueueSnackbar({
          variant: 'error',
          subTitle:
            'This file is currently being validated. Please try again in a couple of minutes.',
          removable: true,
        });
      }
    }, [status]);

    if (typeof err?.to === 'string') {
      return (
        <Navigate
          to={err.to}
          params={err.params ?? { dataFileId }}
          search={err.search as Record<string, string> | undefined}
          replace={err.replace ?? true}
        />
      );
    }

    if ([400, 404, 422, 425].includes(status ?? 0)) {
      return (
        <Navigate
          to="/company-profile/import-hub/performance"
          search={{ tab: 'imports' }}
          replace
        />
      );
    }

    return <InternalServerErrorPage />;
  },
});
